<script lang="ts" setup>
  import { type PropType, ref, computed } from 'vue';
  import { useHttpClient, useUrlGenerator, useTranslator } from '@othercommerce/foundation';
  import { searchTermEvent, useGoogleTagManager } from '@storefront/storefront';

  defineProps({
    price: { type: Object, required: true },
    attributes: { type: Object, required: true },
    brands: { type: Array as PropType<string[]>, required: true },
    availableIn: { type: Array as PropType<number[]>, required: true },
    search: { type: String, required: false },
    options: { type: Object, required: true },
    categories: { type: Object, required: true },
    available: { type: Boolean, required: true },
    total: { type: Number, required: true },
  });

  const open = defineModel('open', { type: Boolean, required: false, default: false });

  const http = useHttpClient();
  const translator = useTranslator();
  const gtm = useGoogleTagManager();
  const url = useUrlGenerator();

  const loading = ref(false);

  const availabilityOptions = computed(() => {
    return [
      { value: 1, label: translator.trans('SHOP.PRODUCT.LABEL.SHOW_ONLY_AVAILABLE') },
    ];
  });

  function submitHandler(data: any) {
    loading.value = true;

    return http.get(url.generate(window.location.pathname, data)).finally(() => {
      loading.value = false;
    });
  }

  function actionSearch(submit: any, phrase: any) {
    submit();

    if (phrase !== undefined && phrase !== null && phrase !== '') {
      gtm.push(searchTermEvent(phrase));
    }
  }

  function actionClear() {
    loading.value = true;

    return http.get(url.generate(window.location.pathname)).finally(() => {
      loading.value = false;
    });
  }
</script>
<template>
  <div class="filters">
    <FormController v-slot="{ submit, data }" :data="{ brands, price, attributes, available, availableIn, search }" :onSubmit="submitHandler">
      <Collapsible ariaControls="filters-search">
        <template #header>
          <SfHeading type="span">
            {{ $t('SHOP.PRODUCT.LABEL.FILTER_SEARCH') }}
          </SfHeading>
        </template>
        <template #content>
          <div class="filters__controls">
            <SfFormInput id="filters-search-input" :label="$t('SHOP.PRODUCT.LABEL.FILTER_SEARCH_PHRASE')" name="search" type="text" @change="actionSearch(submit, data.search)" />
          </div>
        </template>
      </Collapsible>

      <Collapsible v-if="categories.children.length > 0 || categories.category ||  categories.parent" ariaControls="filters-categories">
        <template #header>
          <SfHeading type="span">
            {{ $t('SHOP.PRODUCT.LABEL.FILTER_CATEGORY') }}
          </SfHeading>
        </template>
        <template #content>
          <div class="filters__controls">
            <ShopProductsFilterCategories v-bind="{ categories }" />
          </div>
        </template>
      </Collapsible>

      <Collapsible ariaControls="filters-available" scrollable>
        <template #header>
          <SfHeading type="span">
            {{ $t('SHOP.PRODUCT.LABEL.FILTER_AVAILABILITY') }}
          </SfHeading>
        </template>
        <template #content>
          <SfFormCheckbox :options="availabilityOptions" name="available" @change="submit" />
          <SfFormCheckbox v-if="options.warehouses.length > 1" :options="options.warehouses" name="availableIn" @change="submit" />
        </template>
      </Collapsible>

      <Collapsible v-if="options.brands.length > 0" ariaControls="filters-brand" scrollable>
        <template #header>
          <SfHeading id="brands" type="span">
            {{ $t('SHOP.PRODUCT.LABEL.FILTER_BRAND') }}
          </SfHeading>
        </template>
        <template #content>
          <SfFormCheckbox :options="options.brands" name="brands" @change="submit" />
        </template>
      </Collapsible>

      <Collapsible ariaControls="filters-price">
        <template #header>
          <SfHeading type="span">
            {{ $t('SHOP.PRODUCT.LABEL.FILTER_PRICE') }}
          </SfHeading>
        </template>
        <template #content>
          <div class="filters__controls filters__controls--price">
            <SfFormNumber id="filter-price-min" :label="$t('SHOP.COMMON.LABEL.FROM')" min="0" name="price.min" @change="submit" />
            <SfFormNumber id="filter-price-max" :min="price.min + 1" :label="$t('SHOP.COMMON.LABEL.TO')" name="price.max" @change="submit" />
          </div>
        </template>
      </Collapsible>

      <Collapsible v-for="option in options.attributes" :ariaControls="`collapsible-aria-controls-${option.value}`" scrollable>
        <template #header>
          <SfHeading :id="`attributes.${option.value}`" type="span">
            {{ option.label }}
          </SfHeading>
        </template>
        <template #content>
          <SfFormCheckbox :name="`attributes.${option.value}`" :options="option.options" @change="submit" />
        </template>
      </Collapsible>

      <UiButtonGroup class="filters__buttons-group" end stretch>
        <SfButton :loading class="filters__clear" danger transparent @click="actionClear">
          <template #before>
            <SfIconTrash aria-hidden="true" />
          </template>
          {{ $t('SHOP.PRODUCT.ACTION.FILTER_CLEAN') }}
        </SfButton>
        <SfButton :loading class="filters__button-show" primary @click="open = false">
          {{ $t('SHOP.PRODUCT.ACTION.FILTER_SHOW') }}
          <span>({{ total }})</span>
        </SfButton>
      </UiButtonGroup>
    </FormController>
  </div>
</template>
<style lang="scss">
  @use '@storefront/styles/mixins/media';

  :root {
    --sf-products-filter-gap: 1.5rem;
    --sf-products-filter-item-gap: 0.25rem;
    --sf-products-filter-controls-price-gap: 0.5rem;
    --sf-products-filter-mobile-clear-btn-margin-top: 0;
    --sf-collapsible-input-background: var(--sf-input-background);

    @include media.query(lg) {
      --sf-products-filter-gap: 0;
      --sf-products-filter-item-gap: 0;
      --sf-products-filter-controls-price-gap: 1rem;
      --sf-products-filter-mobile-clear-btn-margin-top: 2rem;
    }
  }

  .filters {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--sf-products-filter-gap);
    --sf-collapsible-gap: var(--sf-products-filter-item-gap);
    --sf-input-background: var(--sf-collapsible-input-background);

    &__controls--price {
      display: flex;
      gap: var(--sf-products-filter-controls-price-gap);
    }

    &__button-show {
      display: none;
    }

    &__buttons-group {
      margin-top: var(--sf-products-filter-mobile-clear-btn-margin-top);

      .btn {
        flex-grow: 1;
      }
    }

    @include media.query(lg) {
      &__button-show {
        display: inline-flex;
      }
    }
  }
</style>
